import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardMedia, Grid, IconButton, MobileStepper, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import AccountCircleTwoToneIcon from '@mui/icons-material/AccountCircleTwoTone';
import CloseIcon from '@mui/icons-material/Close';
import { useMap } from "react-mapkit";
import { AppColor, Fonts } from "../../../../types/Constants";
import { getCategoryColor, getImageURLCategoryListIcon, getImageURLOnServer, getImageURLOnServerBeaconFace } from '../../../../utils/utils';
import { useAuthContext } from "../../../authentication/contexts/AuthContext";
import { Coordinate, GeocoderResponse } from "../../../../types/models/Mapkit";
import { useAppSelector } from "../../../../utils/hooks";
import BeaconCategory from "../../../../components/BeaconCategory";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ImageLoader from "../../../../components/ImageLoader";
import { UploadParameters } from "./BeaconEditor";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";

interface BeaconPreviewProps {
  imageModal?: File,
  imageFace?: File,
  imageId?: string,
  category?: string,
  name?: string,
  beaconId?: string,
  description?: string,
  coordinate?: Coordinate,
  startDate?: string | null,
  hasCustomBeaconFace?: boolean,
  hasLink?: boolean,
  deleteBeaconFace?: boolean,
  lastModified?: string
  imageUploads?: UploadParameters[]
}

const BeaconPreview: React.FC<BeaconPreviewProps> = (prop) => {

  let auth = useAuthContext();
  const theme = useTheme();
  const categoryGroupState = useAppSelector((state) => state.category);

  const { mapkit } = useMap();

  const [imageModalURL, setImageModalURL] = useState<string | undefined>();
  const [imageFaceURL, setImageFaceURL] = useState<string | undefined>();
  const [address, setAddress] = useState<string>();

  const [activeStep, setActiveStep] = useState<number>(0);

  useEffect(() => {
    if (prop.coordinate && prop.coordinate.latitude !== 0 && prop.coordinate.longitude !== 0) {
      reverseLookupAndSetAddress(prop.coordinate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop.coordinate, mapkit]);

  useEffect(() => {
    if (prop.imageModal) {
      // Get file from file instance
      const objectUrl = URL.createObjectURL(prop.imageModal);
      setImageModalURL(objectUrl);
      // free memory when ever this component is unmounted
      return () => URL.revokeObjectURL(objectUrl);
    }
    else if (prop.imageId) {
      // Get imageModal from server
      setImageModalURL(getImageURLOnServer(prop.imageId));
    }
    else {
      setImageModalURL(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop.imageModal, prop.imageId]);

  useEffect(() => {
    if (prop.imageFace) {
      // Get file from file instance
      const objectUrl = URL.createObjectURL(prop.imageFace);
      setImageFaceURL(objectUrl);
      // free memory when ever this component is unmounted
      return () => URL.revokeObjectURL(objectUrl);
    }
    else {
      setImageFaceURL(getImageURLOnServerBeaconFace(prop.beaconId!, prop.lastModified!));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop.imageFace, prop.lastModified]);

  // Update stepper state for edge cases
  useEffect(() => {
    const images = prop.imageUploads ?? [];
    if (activeStep >= images.length && images.length > 0) {
      setActiveStep(images.length - 1);
    } else if (images.length === 0) {
      setActiveStep(0);
    }
  }, [prop.imageUploads, activeStep]);

  const reverseLookupAndSetAddress = (coordinate: Coordinate) => {
    if (mapkit) {
      var geocoder = new mapkit.Geocoder();
      geocoder.getsUserLocation = true; // Lookup from current location
      geocoder.reverseLookup(new mapkit.Coordinate(coordinate.latitude, coordinate.longitude), (error: Error | null, data: GeocoderResponse) => {
        if (error) {
          console.log("Failed to reverseLookup");
          return;
        }

        if (data && data.results && data.results.length > 0) {
          const topResult = data.results[0];
          const addressList = topResult.formattedAddress.split(",");
          let reversedAddress = topResult.formattedAddress;
          if (addressList.length > 2) {
            // Remove country name to be shorter address
            reversedAddress = addressList[0] + ", " + addressList[1];
          }
          setAddress(reversedAddress);
        }
      });
    }
  };

  const getCategory = (categoryName: string) => {
    if (categoryGroupState.categories) {
      const selectedCategory = categoryGroupState.categories.find(category => category.Name === categoryName);
      if (selectedCategory) {
        return selectedCategory;
      }
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const mobilePreview = () => {
    const category = getCategory(prop.category ?? "");
    return (
      <Box className="mobilePreviewContainer" sx={{ display: "flex", justifyContent: "center"}}>
        <Box sx={{ position: "relative", marginTop: "3vh" }}>
          <Box sx={{
            height: "24vh",
            maxHeight: "240px",
            aspectRatio: 3 / 2,
            backgroundColor: "gray",
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}>
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", position: "absolute" }}>
              <Box sx={{ width: "20%", height
              : "4px", margin: 1, backgroundColor: "white", borderRadius: 2 }} />
            </Box>
            <Box sx={{ width: "100%", px: 2, display: "flex", justifyContent: "space-between", position: "absolute", top: 10 }}>
              <IconButton size="small" sx={{
                backgroundColor: AppColor.componentBackground,
                border: "1px solid silver",
                height: 32,
                width: 32,
                '&:hover': {
                  backgroundColor: "white",
                }
              }}>
                <ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
            {imageModalURL && (
                <CardMedia
                  sx={{ height: "100%", objectFit: "fill"}}
                  component="img"
                  src={imageModalURL}
                  alt="beacon"
                />
            )}
          </Box>
          <Box sx={{
            width: "100%",
            position: "absolute",
            aspectRatio: 7 / 8,
            backgroundColor: "white",
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            border: "1px solid silver",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}>
            <Box>
              <Box sx={{
                marginTop: -3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingX: 2
              }}>
                <BeaconCategory 
                  category={category?.Name}
                  justifyContent={"left"}
                  icons={true}
                  id={category?.Id ?? ""}
                />
              </Box>
              <Grid container sx={{ px: 3, marginTop: 2, }}>
                <Grid item xs={7} sx={{ marginTop: 2, }}>
                  <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                    <Tooltip title={address}>
                      <Typography
                        style={{ whiteSpace: "pre-line" }}
                        sx={{
                          fontSize: 11,
                          wordBreak: "break-all",
                          color: AppColor.headerText,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical'
                        }} >{address ?? "Location"}</Typography>
                    </Tooltip>
                    <Tooltip title="Get Directions">
                      <IconButton size="small" sx={{
                        '&:hover': {
                          backgroundColor: AppColor.componentBackground,
                        }
                      }}>
                        <NearMeOutlinedIcon sx={{ width: "20px", height: "20px" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={5} sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", height: "100%" }}>
                    <AccountCircleTwoToneIcon sx={{ width: "20px", height: "20px" }} />
                    <Typography
                      style={{ whiteSpace: "pre-line" }}
                      sx={{
                        fontSize: 11,
                        ml: 1,
                        wordBreak: auth.preferredUserName ? "break-word" : "break-all",
                        color: AppColor.headerText,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: '2',
                        WebkitBoxOrient: 'vertical'
                      }} >{auth.preferredUserName ?? auth.user}</Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ marginTop: 2, marginLeft: 3, alignItems: "start" }}>
                <Typography component="h4"
                  sx={{
                    fontWeight: Fonts.SEMI_BOLD,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '1',
                    WebkitBoxOrient: 'vertical'
                  }}>{prop.name ? prop.name : "Beacon Name"}</Typography>
                <Typography
                  style={{ whiteSpace: 'pre-line' }}
                  sx={{
                    marginTop: 2,
                    fontSize: 13,
                    color: AppColor.subHeaderText,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '3',
                    WebkitBoxOrient: 'vertical'
                  }} >{prop.description ? prop.description : "Beacon details"}</Typography>
              </Box>
            </Box>
            <Box sx={{ margin: 4, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Button
                sx={{
                  paddingX: 6,
                  borderRadius: 4,
                  whiteSpace: "nowrap",
                  color: "white",
                  background: `linear-gradient(to right bottom, ${AppColor.baseOrange}, ${alpha(AppColor.baseOrange, 0.6)})`
                }}>
                Get Directions
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const getTimeFromStringDate = (dateStr?: string) => {
    if (!dateStr || dateStr.length === 0) return "--:--";

    try {
      const date = new Date(dateStr);
      return date.getHours().toString().padStart(2, '0') + ":" + date.getMinutes().toString().padStart(2, '0');
    }
    catch
    {
      return "";
    }
  };

  const pinPreview = () => {
    const category = getCategory(prop.category ?? "");
    const images = prop.imageUploads ?? [];
    const hasImages = images.length > 0;

    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignContent: "center" , alignItems: "center"}}>
          <Card
            sx={{
              width: "402px",
              boxShadow: 8,
              backgroundColor: "white",
              height: "874px",
              display: "flex",
              flexDirection: "column"
            }}>
            <Box> 
              <Box sx={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "350px", minWidth: "240px", overflow: "hidden" }}>
              {hasImages ? (
              <>
              <CardMedia
                sx={{ height: "350px", objectFit: "cover", width: "100%" }}
                component="img"
                src={images[activeStep]?.imageURL}
              />
              <MobileStepper
                variant="dots"
                steps={images.length}
                position="static"
                activeStep={activeStep}
                sx={{ 
                  position: "absolute",
                  bottom: 8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  borderRadius: 20,
                  py: 0,
                  px: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.4)",
                  '& .MuiMobileStepper-dot': {
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '& .MuiMobileStepper-dotActive': {
                    backgroundColor: '#fff',
                  },
                 }}
                nextButton={
                  <Button sx={{ color: "#fff", borderRadius: 12 }} size="small" onClick={handleNext} disabled={activeStep === images.length - 1}>
                    {theme.direction === 'rtl' ? (
                      <KeyboardArrowLeft />
                    ) : (
                      <KeyboardArrowRight />
                    )}
                  </Button>
                }
                backButton={
                  <Button sx={{ color: "#fff", borderRadius: 12 }} size="small" onClick={handleBack} disabled={activeStep === 0}>
                    {theme.direction === 'rtl' ? (
                      <KeyboardArrowRight />
                    ) : (
                      <KeyboardArrowLeft />
                    )}
                  </Button>
                }
              />
              </>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", minHeight: "350px", backgroundColor: `#${category?.Color}1A`}}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center", 
                      alignItems: "center",
                      borderRadius: "50%",
                      width: "192px",
                      height: "192px",
                      border: "6px solid " + getCategoryColor(prop.category ?? "", categoryGroupState.categories ?? []),
                      backgroundColor: AppColor.componentBackground,
                    }}>
                    <Box sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                      {/* {(prop.hasCustomBeaconFace && !prop.deleteBeaconFace) || prop.imageFace ? (
                        <ImageLoader 
                          imageURL={imageFaceURL ?? ""}
                          borderRadius={"50%"}
                          invertColor={true}
                        />
                      ) : (
                        <ImageLoader 
                        imageURL={getImageURLCategoryListIcon(category?.Id ?? "", category?.LastModified ?? "")}
                        borderRadius={"50%"}
                        invertColor={true}
                      />
                      )} */}
                      <ImageLoader 
                        imageURL={getImageURLCategoryListIcon(category?.Id ?? "", category?.LastModified ?? "")}
                        borderRadius={"50%"}
                        invertColor={true}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
                <IconButton
                  key="close"
                  aria-label="close"
                  color="inherit"
                  size="small"
                  sx={{ 
                    color: "white", 
                    backgroundColor: AppColor.darkBrand,
                    height: "30px", 
                    position: "absolute",
                    top: 8,
                    right: 8
                  }}
                >
                  <CloseIcon sx={{ width: "20px" }} />
                </IconButton>
              </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 3}}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    border: "2px solid " + getCategoryColor(prop.category ?? "", categoryGroupState.categories ?? []),
                    backgroundColor: AppColor.componentBackground,
                    boxSizing: "border-box",
                  }}>
                      <ImageLoader 
                        imageURL={getImageURLCategoryListIcon(category?.Id ?? "", category?.LastModified ?? "")}
                        borderRadius={"50%"}
                        invertColor={true}
                      />
                </Box>
                <Typography component="h4"
                  sx={{
                    fontWeight: 500,
                    fontSize: "18px",
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '1',
                    WebkitBoxOrient: 'vertical'
                  }}>{prop.category ? prop.category : "..."}</Typography>
              </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography component="h4"
                      sx={{
                        fontWeight: Fonts.REGULAR,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: '1',
                        WebkitBoxOrient: 'vertical',
                        fontSize: 16,
                      }}>~5m</Typography>
                  <NearMeOutlinedIcon sx={{ fontSize: 18, display: '-webkit-box', WebkitLineClamp: '1',
                        WebkitBoxOrient: 'vertical', }}/>
                </Box>
              </Box>
                <Typography component="h4"
                  sx={{
                    fontWeight: Fonts.SEMI_BOLD,
                    fontSize: "22px",
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    paddingX: 5,
                    WebkitLineClamp: '1',
                    WebkitBoxOrient: 'vertical'
                  }}>{prop.name ? prop.name : "Beacon Name"}
                </Typography>
              <Box sx={{ 
                  display: "flex", 
                  paddingY: 2, 
                  paddingX: 5, 
                  minWidth: "240px", 
                  overflow: "auto", 
                  height: "355px", 
                  scrollbarWidth: "none",
                  position: "relative",
                }}
              >
              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Typography
                  style={{ whiteSpace: "pre-line" }}
                  sx={{
                    fontSize: 16,
                    color: AppColor.headerText,
                    wordBreak: "break-word",
                    scrollbarWidth: "none",
                  }} >{prop.description ? prop.description : "..."}
                </Typography>
              </Box>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "50px",
                    background: "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%)",
                    zIndex: 1, 
                  }}
                />
              </Box>
            </Box>
            {prop.hasLink && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 6, height: "32px" }}>
              
                <Button 
                variant="contained"
                sx={{
                  borderRadius: "32px",
                  height: "32px",
                  width: "65%",
                  textAlign: "center",
                }}
              >Open Link<ChevronRightIcon sx={{ ml: 2 }} />
              </Button>
            </Box>
            )}  
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "flex-end", flexGrow: 1, mb: 1 }}>
                  <Box sx={{ width: "33%", height: "5px", backgroundColor: "black", borderRadius: 2, }} />
              </Box>
          </Card>
      </Box>
    );
  };

  return (
    <Box sx={{
      height: "100vh",
      width: "100%",
      backgroundColor: "white",
      minWidth: "370px",
      display: "flex",
      justifyContent: "center",
      alignItems: "start",
      marginTop: 14
    }}>
      {pinPreview()}
    </Box >
  );
};

export default BeaconPreview;
